// https://codepen.io/ilikescience/pen/mddOWWg
// https://css-tricks.com/making-an-audio-waveform-visualizer-with-vanilla-javascript/

/**
 * Fetches audio from a given source, and draws the waveform with given parameters to the given canvas.
 *
 * Browser autoplay policy may cause failure; Promise resolves as true upon success
 *
 * @returns {Promise<boolean>} Did we succeed in drawing?
 */
export default async function drawWaveform({
  canvas,
  src,
  stroke = 2,
  samples = 80
}: {
  canvas: HTMLCanvasElement;
  src: string;
  stroke: number;
  samples: number;
}): Promise<boolean> {
  try {
    const audioContext = new AudioContext();

    await fetch(src)
      .then(response => response.arrayBuffer())
      .then(arrayBuffer => audioContext.decodeAudioData(arrayBuffer))
      .then(audioBuffer =>
        draw(canvas, normalizeData(filterData(audioBuffer, samples)), stroke)
      );

    return true;
  } catch (e) {
    console.error(`Failed to draw audio waveform: ${src} - ${e.message}`);
    return false;
  }
}

/**
 * Creates a sample waveform pattern for when audio cannot be loaded.
 * @param {HTMLCanvasElement} canvas The canvas to draw on
 * @param {number} stroke Stroke width for the waveform
 * @returns {boolean} Did we succeed in drawing?
 */
export function drawSampleWaveform(
  canvas: HTMLCanvasElement,
  stroke: number
): boolean {
  // confirm there is a canvas
  if (!canvas) return false;

  // 80 sample values copied from a random voicemail file
  const normalizedData = [
    0.8301854723093375, 0.8436269033456707, 0.9179709556406328,
    0.7175949790347395, 0.5954797440482288, 0.50695698448299,
    0.5968013957324145, 0.7683816455791062, 0.6364366684308768,
    0.7107430503722917, 0.5758690661601904, 0.4834638401426641,
    0.13630630708932767, 0.5736322676907991, 0.7436880309785086,
    0.43907014353867385, 0.833728163268933, 0.7399473757634367,
    0.5819371230440982, 0.5150563517104236, 0.7715420626267785,
    0.7323585090934563, 0.7054451348537218, 0.6978645844452225,
    0.6925242150967598, 0.6806688664001499, 0.9071895044632059,
    0.6377842084823558, 0.5864654560933017, 0.9361219617344264,
    0.4801784123058081, 0.03410460773451221, 0.7690532906226478,
    0.9213698928480076, 0.6962471049697444, 0.7613158450489982,
    0.6640315556159699, 0.6851906322999095, 0.5626907839145616,
    0.7464472348341944, 0.7183574188616171, 0.4327664818474935, 1,
    0.7273273910121512, 0.8928219566964363, 0.5962502851182606,
    0.13551798381783542, 0.7303281066910425, 0.8048265093751629,
    0.8091207400173304, 0.7340691794448926, 0.594708067995465,
    0.5410128283145079, 0.03325121921352701, 0.6447699669771112,
    0.6818881874441903, 0.6474301991176209, 0.51343432364193,
    0.9155239385538231, 0.8406692156158238, 0.510626651959037,
    0.6459838420474743, 0.4904186639911434, 0.6614232752439142,
    0.5570276323491954, 0.3814773711717895, 0.5610197063336165,
    0.7543804488410998, 0.7267943260435855, 0.7091467152991783,
    0.7200468639492089, 0.6178229403396671, 0.637849687666383,
    0.6134487686258671, 0.4894049930783591, 0.49375782254855655,
    0.0018018274203791406, 0.6973573354750138, 0.8087994303914262
  ];

  try {
    // Draw using the same drawing logic
    draw(canvas, normalizedData, stroke);

    return true;
  } catch (e) {
    console.error(`Failed to draw sample waveform: ${e.message}`);
    return false;
  }
}

/**
 * Filters the AudioBuffer retrieved from an external source
 * @param {AudioBuffer} audioBuffer the AudioBuffer from drawWaveform()
 * @returns {Array} an array of floating point numbers
 */
const filterData = (audioBuffer: AudioBuffer, samples: number): number[] => {
  const rawData = audioBuffer.getChannelData(0); // We only need to work with one channel of data
  // const samples = 100; // Number of samples we want to have in our final data set
  const blockSize = Math.floor(rawData.length / samples); // the number of samples in each subdivision
  const filteredData: number[] = [];
  for (let i = 0; i < samples; i++) {
    const blockStart = blockSize * i; // the location of the first sample in the block
    let sum = 0;
    for (let j = 0; j < blockSize; j++) {
      sum = sum + Math.abs(rawData[blockStart + j]); // find the sum of all the samples in the block
    }
    filteredData.push(sum / blockSize); // divide the sum by the block size to get the average
  }

  // console.log({ filteredData });

  return filteredData.map(n => Math.sqrt(n));
};

/**
 * Normalizes the audio data to make a cleaner illustration
 * @param {Array} filteredData the data from filterData()
 * @returns {Array} an normalized array of floating point numbers
 */
const normalizeData = (filteredData: number[]): number[] => {
  const multiplier = Math.pow(Math.max(...filteredData), -1);

  const normalized = filteredData.map(n => n * multiplier);

  // console.log( normalized );
  return normalized;
};

/**
 * Draws the audio file into a canvas element.
 * @param {Array} normalizedData The filtered array returned from filterData()
 * @returns {Array} a normalized array of data
 */
const draw = (
  canvas: HTMLCanvasElement,
  normalizedData: number[],
  stroke: number
): void => {
  // Use getBoundingClientRect for more accurate sizing, especially during animations
  const rect = canvas.getBoundingClientRect();
  const canvasWidth = rect.width;
  const canvasHeight = rect.height;

  // confirm there is a canvas with some layout
  if (!(canvasWidth > 0) || !(canvasHeight > 0)) {
    return;
  }

  // set up the canvas
  const dpr = window.devicePixelRatio || 1;
  const padding = 20;
  canvas.width = canvasWidth * dpr;
  canvas.height = (canvasHeight + padding * 2) * dpr;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }
  ctx.scale(dpr, dpr);
  ctx.translate(0, canvasHeight / 2 + padding); // set Y = 0 to be in the middle of the canvas

  // draw the line segments
  const width = (canvasWidth - stroke * 2) / normalizedData.length;
  for (let i = 0; i < normalizedData.length; i++) {
    const x = width * i + stroke;
    let height = normalizedData[i] * canvasHeight - padding;
    if (height < 0) {
      height = 0;
    } else if (height > canvasHeight / 2) {
      height = 1;
    }
    drawLineSegment(ctx, x, height, width, (i + 1) % 2 === 1, stroke);
  }
};

/**
 * A utility function for drawing our line segments
 * @param {CanvasRenderingContext2D} ctx the canvas context
 * @param {number} x  the x coordinate of the beginning of the line segment
 * @param {number} height the desired height of the line segment
 * @param {number} width the desired width of the line segment
 * @param {boolean} isEven whether or not the segmented is even-numbered
 * @param {number} stroke the stroke width
 */
const drawLineSegment = (
  ctx: CanvasRenderingContext2D,
  x: number,
  height: number,
  width: number,
  isEven: boolean,
  stroke: number
): void => {
  ctx.lineWidth = stroke; // how thick the line is
  ctx.strokeStyle = '#fff'; // what color our line is
  ctx.beginPath();
  height = isEven ? height : -height;
  ctx.moveTo(x, 0);
  ctx.lineTo(x, height);
  ctx.arc(x + width / 2, height, width / 2, Math.PI, 0, isEven);
  ctx.lineTo(x + width, 0);
  ctx.stroke();
};

// document.querySelectorAll('[data-waveform]').forEach(drawWaveform);
